import { ChangeDetectionStrategy, Component, HostBinding, Inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ThemeOptions } from '@frontend/sports/common/ui/shared';
import { DsButtonKind, DsButtonVariant } from '@frontend/ui/button';
import { EpcotConfigService, EpcotModule } from 'packages/sports/web/app/src/common/epcot-config.service';
import { Observable, map } from 'rxjs';

import { flagEnumContainsValue } from '../../../../base/utils/flag-enum-contains-value-utils';
import { PaymentMethod } from '../../../quick-deposit/quick-deposit.models';
import { isUpdateStakeState } from '../../services/place-button/place-button-state.utils';
import {
    IPlaceButtonViewModel,
    IPlaceButtonViewModelService,
    PLACE_BUTTON_VIEWMODEL_SERVICE,
} from '../../services/place-button/viewmodel/interfaces/place-button-interfaces';
import { IPlaceButtonState, PlaceButtonStates } from '../../state';

export type BetslipButtonActionPredicate = () => boolean | void;
type ButtonConfig = {
    kind: DsButtonKind;
    variant: DsButtonVariant;
    themeKind: ThemeOptions<DsButtonKind>;
    themeVariant: ThemeOptions<DsButtonVariant>;
};

@Component({
    selector: 'bs-digital-place-button',
    templateUrl: './place-button.component.html',
    styleUrls: ['./place-button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PlaceButtonComponent {
    @HostBinding('class') className = 'betslip-place-button';

    button$: Observable<IPlaceButtonViewModel & { config: ButtonConfig }>;
    paymentMethod = PaymentMethod;

    isEpcotEnabled: boolean;

    constructor(
        @Inject(PLACE_BUTTON_VIEWMODEL_SERVICE) buttonViewModelService: IPlaceButtonViewModelService,
        epcotConfigService: EpcotConfigService,
    ) {
        this.isEpcotEnabled = epcotConfigService.isEnabled(EpcotModule.Betslip);
        this.button$ = buttonViewModelService.viewModel$.pipe(
            map((viewModel) => ({ ...viewModel, config: this.getButtonConfig(viewModel.state) })),
            takeUntilDestroyed(),
        );
    }

    readonly buttonConfigs: {
        normal: ButtonConfig;
        place: ButtonConfig;
        accept: ButtonConfig;
    } = {
        normal: {
            kind: 'primary',
            variant: 'filled',
            themeKind: {},
            themeVariant: {},
        },
        place: {
            kind: 'success',
            variant: 'filled',
            themeKind: { 'mgm-4': 'primary', 'sports-interaction': 'primary', 'oddset': 'primary' },
            themeVariant: { 'mgm-4': 'filled', 'sports-interaction': 'filled', 'oddset': 'filled' },
        },
        accept: {
            kind: 'secondary',
            variant: 'outline',
            themeKind: {
                'mgm-4': 'primary',
                'sports-interaction': 'primary',
                'sportingbet-br': 'primary',
                'betboo-br': 'primary',
            },
            themeVariant: {
                'mgm-4': 'filled',
                'sports-interaction': 'filled',
                'sportingbet-br': 'filled',
                'betboo-br': 'filled',
            },
        },
    };

    getButtonConfig(betslipState: IPlaceButtonState) {
        if (betslipState.isProcessing) {
            // eslint-disable-next-line no-bitwise
            if (flagEnumContainsValue(betslipState.action, PlaceButtonStates.Deposit)) {
                return this.buttonConfigs.normal;
            }

            return this.buttonConfigs.place;
        }
        if (isUpdateStakeState(betslipState.action)) {
            return this.buttonConfigs.normal;
        }
        // eslint-disable-next-line no-bitwise
        if (flagEnumContainsValue(betslipState.action, PlaceButtonStates.PlaceBet)) {
            return this.buttonConfigs.place;
        }
        // eslint-disable-next-line no-bitwise
        if (flagEnumContainsValue(betslipState.action, PlaceButtonStates.AcceptChanges)) {
            if (betslipState.isLinear) {
                return this.buttonConfigs.normal;
            }

            return this.buttonConfigs.accept;
        }
        // eslint-disable-next-line no-bitwise
        if (flagEnumContainsValue(betslipState.action, PlaceButtonStates.Deposit)) {
            return this.buttonConfigs.normal;
        }
        // eslint-disable-next-line no-bitwise
        if (flagEnumContainsValue(betslipState.action, PlaceButtonStates.Confirm)) {
            return this.buttonConfigs.normal;
        }

        return this.buttonConfigs.normal;
    }

    ShowMessageInButtonEpcot(betslipState: IPlaceButtonState): boolean {
        return betslipState.isLinear || isUpdateStakeState(betslipState.action);
    }
}
